import { useEffect, useState } from "react";
import { Zap, Save, PlugZap } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Field } from "@/components/wizard/Field";
import { t, fmtDateTime } from "@/i18n";

export const SoundlinkSettings = ({ admin }) => {
  const [s, setS] = useState(null);
  const [key, setKey] = useState("");
  const [f, setF] = useState({ auto_create: true, domain: "", release_tz: "America/Mexico_City" });
  const [testing, setTesting] = useState(false);
  const apply = (d) => { setS(d); setF({ auto_create: d.auto_create, domain: d.domain || "", release_tz: d.release_tz || "America/Mexico_City" }); };
  useEffect(() => { api.get("/settings/soundlink").then((r) => apply(r.data)).catch(() => {}); }, []);

  const save = async () => {
    try { const { data } = await api.put("/settings/soundlink", { ...f, api_key: key || undefined }); apply(data); setKey(""); toast.success(t("Ajustes de SoundLinks guardados")); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const test = async () => {
    setTesting(true);
    try { await api.post("/settings/soundlink/test"); toast.success(t("Conexión con SoundLinks correcta")); api.get("/settings/soundlink").then((r) => apply(r.data)); }
    catch (e) { toast.error(errMsg(e)); } finally { setTesting(false); }
  };

  return (
    <section className="lg:col-span-7 panel p-6 sm:p-8 space-y-5" data-testid="soundlink-settings">
      <div className="flex items-start gap-3">
        <div className="h-10 w-10 rounded-md bg-emerald-500/10 text-emerald-400 grid place-items-center shrink-0"><Zap className="h-5 w-5" /></div>
        <div><h2 className="font-display text-lg font-semibold">SoundLinks <span className="text-xs font-normal text-muted-foreground">soundlinks.ink</span></h2><p className="text-sm text-muted-foreground">{t("En cuanto un lanzamiento recibe su UPC se crea el pre-save por API; el día del estreno se convierte solo en smartlink con todas las plataformas. Cada artista queda como perfil dentro de tu cuenta de distribuidora.")}</p></div>
      </div>
      {s && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs" data-testid="soundlink-status">
          {[[t("API key"), s.has_api_key ? s.key_hint : "—"], [t("Última verificación"), s.last_ok_at ? fmtDateTime(s.last_ok_at) : t("nunca")], [t("Dominio"), s.domain || "soundlinks.ink"], [t("Crear automáticamente"), s.auto_create ? t("Sí") : "No"]].map(([k, v]) => (
            <div key={k} className="rounded-md border border-border p-3"><p className="eyebrow">{k}</p><p className="font-medium mt-1 truncate">{v}</p></div>
          ))}
        </div>
      )}
      {s?.last_error && <p className="text-xs text-destructive" data-testid="soundlink-last-error">{s.last_error}</p>}
      <Field label="API key de SoundLinks" hint={s?.has_api_key ? t("Déjalo vacío para conservar la actual. Se crea en soundlinks.ink → Admin → Claves de API.") : t("Se crea en soundlinks.ink → Admin → Claves de API.")}>
        <Input type="password" value={key} onChange={(e) => setKey(e.target.value)} placeholder="slk_…" disabled={!admin} autoComplete="new-password" data-testid="soundlink-key-input" />
      </Field>
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Dominio por defecto (opcional)" hint={t("Debe estar activo en SoundLinks. Si el artista tiene su propio dominio de smartlink, se usa el suyo.")}>
          <Input value={f.domain} onChange={(e) => setF({ ...f, domain: e.target.value })} placeholder="links.doblerrecords.com" disabled={!admin} data-testid="soundlink-domain" />
        </Field>
        <Field label="Zona horaria del estreno" hint={t("Hora a la que el pre-save pasa a smartlink (00:00 de la fecha de lanzamiento).")}>
          <Input value={f.release_tz} onChange={(e) => setF({ ...f, release_tz: e.target.value })} placeholder="America/Mexico_City" disabled={!admin} data-testid="soundlink-tz" />
        </Field>
      </div>
      <label className="flex items-center justify-between gap-3 rounded-md border border-border p-3 text-sm"><span>{t("Crear pre-save / smartlink automáticamente al asignarse el UPC")}</span><Switch checked={f.auto_create} onCheckedChange={(v) => setF({ ...f, auto_create: v })} disabled={!admin} data-testid="soundlink-autocreate" /></label>
      {admin && (
        <div className="flex flex-wrap gap-2">
          <Button onClick={save} className="gap-2" data-testid="soundlink-save"><Save className="h-4 w-4" /> {t("Guardar")}</Button>
          <Button variant="outline" onClick={test} disabled={testing || !s?.has_api_key} className="gap-2" data-testid="soundlink-test"><PlugZap className="h-4 w-4" /> {t(testing ? "Verificando…" : "Verificar conexión")}</Button>
        </div>
      )}
    </section>
  );
};
